import { forwardRef, type CSSProperties, type ElementType, type HTMLAttributes, type ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'

/** Sınıf adlarını birleştirir; boş/yanlış değerleri atar. */
export function cx(...parts: Array<string | number | boolean | null | undefined>): string {
  return parts.filter((p) => typeof p === 'string' && p).join(' ')
}

type Gap = number | string
const px = (v: Gap | undefined) => (typeof v === 'number' ? `${v}px` : v)

interface FlexProps extends HTMLAttributes<HTMLElement> {
  gap?: Gap
  align?: CSSProperties['alignItems']
  justify?: CSSProperties['justifyContent']
  wrap?: boolean
  grow?: boolean
  as?: ElementType
}

/** Dikey yığın (flex column). */
export const Stack = forwardRef<HTMLElement, FlexProps>(function Stack(
  { gap = 12, align, justify, grow, as: As = 'div', className, style, ...rest }, ref,
) {
  return (
    <As ref={ref} className={cx('ui-stack', className)}
      style={{ gap: px(gap), alignItems: align, justifyContent: justify, flex: grow ? '1 1 auto' : undefined, ...style }}
      {...rest} />
  )
})

/** Yatay sıra (flex row, ortalı). */
export const Row = forwardRef<HTMLElement, FlexProps>(function Row(
  { gap = 8, align, justify, wrap, grow, as: As = 'div', className, style, ...rest }, ref,
) {
  return (
    <As ref={ref} className={cx('ui-row', wrap && 'ui-row--wrap', className)}
      style={{ gap: px(gap), alignItems: align, justifyContent: justify, flex: grow ? '1 1 auto' : undefined, ...style }}
      {...rest} />
  )
})

export function Spacer() {
  return <span className="ui-spacer" aria-hidden="true" />
}

interface TextProps extends HTMLAttributes<HTMLElement> {
  size?: 'xs' | 'sm' | 'md' | 'lg'
  muted?: boolean
  faint?: boolean
  strong?: boolean
  truncate?: boolean
  mono?: boolean
  as?: ElementType
  color?: string
}

export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { size = 'md', muted, faint, strong, truncate, mono, as: As = 'p', color, className, style, ...rest }, ref,
) {
  return (
    <As ref={ref}
      className={cx('ui-text', `ui-text--${size}`, muted && 'ui-text--muted', faint && 'ui-text--faint',
        strong && 'ui-text--strong', truncate && 'ui-text--truncate', mono && 'ui-text--mono', className)}
      style={color ? { color, ...style } : style} {...rest} />
  )
})

interface TitleProps extends HTMLAttributes<HTMLHeadingElement> {
  order?: 1 | 2 | 3 | 4
  /** Görsel boyut; verilmezse `order` ile aynı. */
  size?: 1 | 2 | 3 | 4
}

export function Title({ order = 2, size, className, ...rest }: TitleProps) {
  const H = `h${order}` as ElementType
  return <H className={cx('ui-title', `ui-title--${size ?? order}`, className)} {...rest} />
}

export function Eyebrow({ className, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cx('ui-eyebrow', className)} {...rest} />
}

export function Code({ block, className, ...rest }: HTMLAttributes<HTMLElement> & { block?: boolean }) {
  return block
    ? <pre className={cx('ui-code', 'ui-code--block', className)} {...rest} />
    : <code className={cx('ui-code', className)} {...rest} />
}

export function Kbd({ className, ...rest }: HTMLAttributes<HTMLElement>) {
  return <kbd className={cx('ui-kbd', className)} {...rest} />
}

export function Divider({ className, ...rest }: HTMLAttributes<HTMLHRElement>) {
  return <hr className={cx('ui-divider', className)} {...rest} />
}

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="ui-visually-hidden">{children}</span>
}

export function TextLink({ className, ...rest }: LinkProps) {
  return <Link className={cx('ui-link', className)} {...rest} />
}
